import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Subpage from "../components/Subpage";
import { Heart, MessageCircle } from "lucide-react";
import { API_BASE, mediaUrl, hitView } from "../utils/apiBase";
import { renderStrapiBlocks } from "../utils/renderStrapiBlocks";


// 🔧 Extract YouTube video ID from full URL
function extractYouTubeID(url) {
  const match = url?.match(
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/
  );
  return match ? match[1] : null;
}

function NewsPost() {
  const { slugOrId, documentId } = useParams();
  const param = slugOrId || documentId;   // final value we’ll use
  const [post, setPost] = useState(null);
  const [item, setItem] = useState(null);

  /* tracks which documentId we already sent a hit for */
  const hitSentFor = useRef(null);
  
  // const [likes, setLikes] = useState(0); // 💤 temporarily disabled (likes)

  useEffect(() => {
  axios
    .get(`${API_BASE}/news-items`, {
      params: {
        'filters[$or][0][slug][$eq]': param,
        'filters[$or][1][documentId][$eq]': param,
        populate: { Sections: { populate: 'Media' } },
    },
    })
    .then(async (res) => {
      const item = res.data.data[0];
      setPost(item);

    /* ─── bump view counter once per tab session ─── */
      if (item?.documentId && hitSentFor.current !== item.documentId) {
        hitSentFor.current = item.documentId;
        const { skipped } = await hitView(item.documentId);

        if (!skipped) {
          setPost(prev => ({
            ...prev,
            attributes: {
              ...prev.attributes,
              views: (prev.attributes?.views ?? 0) + 1,
            },
            views: (prev.views ?? prev.attributes?.views ?? 0) + 1,
          }));
        }
      }
    })
    .catch(console.error);
}, [param]);

/* 
// 💬 sendLike handler (disabled for now)
const sendLike = async () => {
  try {
    await axios.post(`${API_BASE}/api/news-items/${post.id}/like`);
    setLikes((l) => l + 1);
  } catch (err) {
    console.error("like error", err);
  }
};
*/


  if (!post) {
    return (
      <Subpage title="Loading..." content="Please wait.">
        <p>Loading post...</p>
      </Subpage>
    );
  }

  const postData = post;

  // Safe access to fields (no .attributes wrapper in your setup)
  const {
    Title,
    PublishDate,
    Links,
    Sections,
  } = postData;
  
  function renderBlock(block, index) {

    const { TextContent, Media, YoutubeURL } = block;
  
    const renderText = Array.isArray(TextContent)
      ? renderStrapiBlocks(TextContent)
      : null;

  
    const renderImages =
    Array.isArray(Media) && Media.length > 0 ? (
      <div className="mb-8 space-y-8">
        {Media.map((file, i) => {
          const imageUrl = file?.url || file?.attributes?.url;
          return imageUrl ? (
            <img
              key={i}
              src={mediaUrl(imageUrl)}
              alt={`Screenshot ${i + 1}`}
              className="w-full"
            />
          ) : (
            <p key={i} className="text-red-500">⚠️ Missing image URL</p>
          );
        })}
      </div>
    ) : null;
  
  
    const renderYouTube = YoutubeURL ? (() => {
      const id = extractYouTubeID(YoutubeURL);
      return id ? (
        <div className="aspect-video mb-8">
          <iframe
            className="w-full h-full rounded-md"
            src={`https://www.youtube.com/embed/${id}`}
            title={`YouTube video`}
            allowFullScreen
          />
        </div>
      ) : null;
    })() : null;
  
    return (
      <div key={index} className="mb-12">
        {renderText}
        {renderImages}
        {renderYouTube}
      </div>
    );
  }

  return (
    <Subpage
      title={Title}
      content={`Published on ${PublishDate}`}
      relatedLinks={[]}
    >
      <div className="max-w-3xl mx-auto mb-16">   {/*  mb-16 ≈ 4rem space */}
        {/* Flexible News Sections */}
{Array.isArray(Sections) && Sections.map(renderBlock)}

        {/* Like & Comment bar (disabled for now) */}
        {/*
          <div className="mt-6 flex items-center gap-6 text-black/80 text-sm">
            <span className="flex items-center gap-1">
              <Heart
              className="w-5 h-5 cursor-pointer hover:scale-110 transition"
              onClick={post ? sendLike : undefined}
              />
              {likes}
            </span>

            <span className="flex items-center gap-1">
              <MessageCircle className="w-5 h-5" />
              {post.comments?.length ?? 0}
            </span>
          </div>
        */}

        {/* External Link */}
        {Links && (
          <a
            href={Links}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-5 py-3 bg-red-600 text-white rounded hover:bg-red-700 transition"
          >
            Read more →
          </a>
        )}
      </div>
    </Subpage>
  );
}

export default NewsPost;
